/**
 * Slippage configuration and helper functions
 * Must match backend/engine/src/vlp.rs tiers exactly
 */

export interface SlippageTier {
  minSizeUsd: number;
  additionalBps: number;
}

// Progressive slippage tiers - must match backend exactly
export const SLIPPAGE_TIERS: readonly SlippageTier[] = [
  { minSizeUsd: 10_000, additionalBps: 0.5 },
  { minSizeUsd: 100_000, additionalBps: 2.0 },
  { minSizeUsd: 1_000_000, additionalBps: 5.0 },
  { minSizeUsd: 10_000_000, additionalBps: 10.0 },
] as const;

/**
 * Calculate additional spread from order size based on progressive tiers
 * @param orderSizeUsd Order size in USD
 * @returns Additional spread in basis points
 */
export function getAdditionalSpreadBps(orderSizeUsd: number): number {
  let additionalBps = 0;
  
  for (const tier of SLIPPAGE_TIERS) {
    if (orderSizeUsd >= tier.minSizeUsd) {
      additionalBps = Math.max(additionalBps, tier.additionalBps);
    }
  }
  
  return additionalBps;
}

/**
 * Smart tolerance suggestions based on order size
 * Recommends minimum tolerance needed for order to execute
 * More conservative thresholds to ensure orders execute reliably
 * @param orderSizeUsd Order size in USD
 * @returns Suggested tolerance as decimal (e.g., 0.01 = 1%)
 */
export function getSuggestedTolerance(orderSizeUsd: number): number {
  if (orderSizeUsd >= 5_000_000) return 0.01;    // 1% for $5M+ orders
  if (orderSizeUsd >= 500_000) return 0.005;     // 0.5% for $500k+ orders
  if (orderSizeUsd >= 50_000) return 0.0025;     // 0.25% for $50k+ orders
  return 0.001; // 0.1% for orders under $50k
}

/**
 * Preset tolerance options for dialog (as decimals)
 * Adjusted to realistic values based on progressive slippage tiers
 */
export const TOLERANCE_PRESETS = [
  0.001,   // 0.1%
  0.0025,  // 0.25%
  0.005,   // 0.5%
  0.01,    // 1%
] as const;

/**
 * Format tolerance as percentage string
 * @param tolerance Tolerance as decimal (e.g., 0.01), or null for Auto
 * @returns Formatted string (e.g., "1%" or "Auto")
 */
export function formatTolerancePercent(tolerance: number | null): string {
  if (tolerance === null) return "Auto";
  return `${(tolerance * 100).toFixed(tolerance >= 0.01 ? 1 : 2)}%`;
}

/**
 * Format basis points as string
 * @param bps Basis points
 * @returns Formatted string (e.g., "1.1 bps")
 */
export function formatBasisPoints(bps: number): string {
  return `${bps.toFixed(bps >= 10 ? 1 : 2)} bps`;
}
